import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, MailOpen, Mail, CheckCircle2, RotateCcw, Search, Reply } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { Pager, usePaged } from "@/components/Pager";
import { api, errMsg, fmtDate } from "@/lib/api";

const TABS = [["open", "Open"], ["resolved", "Resolved"], ["all", "All"]];

const EmailStatus = ({ m }) => (!m.email_status || m.email_status === "not_sent" ? null : (
  <span className={`font-semibold ${m.email_status === "sent" ? "text-emerald-700" : "text-[#E60000]"}`} data-testid={`contact-message-email-status-${m.id}`}>
    · {m.email_status === "sent" ? "Emailed" : m.email_status === "partial" ? "Some emails failed" : "Email failed"}
  </span>
));

const Message = ({ m, open, onToggle, onResolve, onDelete, busy }) => (
  <div className={`bg-white border rounded-lg transition-colors ${m.resolved ? "border-emerald-200" : m.read ? "border-[#E5E7EB]" : "border-[#E60000]/40"}`} data-testid={`contact-message-${m.id}`}>
    <div className="flex items-start gap-3 p-4">
      <button onClick={onToggle} className="flex-1 min-w-0 text-left flex items-start gap-3" data-testid={`contact-message-open-${m.id}`}>
        {m.resolved ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" /> : m.read ? <MailOpen className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" /> : <Mail className="w-5 h-5 text-[#E60000] mt-0.5 shrink-0" />}
        <div className="min-w-0">
          <div className={`text-sm ${m.read ? "" : "font-semibold"}`}>{m.subject || "(no subject)"} <span className="text-gray-500 font-normal">— {m.name}</span></div>
          <div className="text-xs text-gray-500 mt-0.5">{m.email}{m.phone && ` · ${m.phone}`} · {fmtDate(m.created_at)} <EmailStatus m={m} /></div>
          {m.resolved && <div className="text-xs text-emerald-700 mt-0.5" data-testid={`contact-message-resolved-${m.id}`}>Resolved {fmtDate(m.resolved_at)}{m.resolved_by ? ` by ${m.resolved_by}` : ""}</div>}
          {!open && <div className="text-sm text-gray-600 mt-1 line-clamp-1">{m.message}</div>}
        </div>
      </button>
      <div className="flex items-center gap-1 shrink-0">
        {m.resolved
          ? <BrandButton variant="outline" onClick={() => onResolve(false)} disabled={busy} data-testid={`contact-message-reopen-${m.id}`}><RotateCcw className="w-4 h-4" /><span className="hidden sm:inline">Reopen</span></BrandButton>
          : <BrandButton onClick={() => onResolve(true)} disabled={busy} data-testid={`contact-message-resolve-${m.id}`}><CheckCircle2 className="w-4 h-4" /><span className="hidden sm:inline">Mark resolved</span></BrandButton>}
        <button onClick={onDelete} aria-label={`Delete message from ${m.name}`} className="w-9 h-9 grid place-items-center rounded-md text-gray-400 hover:text-[#E60000] hover:bg-[#FFF0F0]" data-testid={`contact-message-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
    {open && (
      <div className="px-4 pb-4 sm:pl-12" data-testid={`contact-message-body-${m.id}`}>
        <div className="rounded-md bg-[#F8F9FA] p-4 text-sm whitespace-pre-wrap">{m.message}</div>
        <a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject || "Your message to GAMLS"}`)}`} className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-[#E60000] hover:underline" data-testid={`contact-message-reply-${m.id}`}><Reply className="w-3.5 h-3.5" />Reply by email</a>
      </div>
    )}
  </div>
);

export default function ContactMessages() {
  const [msgs, setMsgs] = useState(null);
  const [tab, setTab] = useState("open");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/contact-messages").then((r) => setMsgs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const replace = (m) => setMsgs((list) => list.map((x) => (x.id === m.id ? m : x)));
  const toggle = (m) => {
    setOpen(open === m.id ? null : m.id);
    if (!m.read) { api.patch(`/contact-messages/${m.id}`, { read: true }).then((r) => replace(r.data)).catch(() => {}); }
  };
  const changed = () => window.dispatchEvent(new Event("contact-messages-changed"));
  const resolve = async (m, resolved) => {
    setBusy(true);
    try { replace((await api.patch(`/contact-messages/${m.id}`, { resolved })).data); changed(); toast.success(resolved ? "Message marked as resolved" : "Message reopened"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const counts = { open: (msgs || []).filter((m) => !m.resolved).length, resolved: (msgs || []).filter((m) => m.resolved).length, all: (msgs || []).length };
  const rows = useMemo(() => (msgs || []).filter((m) => (tab === "all" || (tab === "resolved") === !!m.resolved)
    && `${m.name} ${m.email} ${m.subject} ${m.message}`.toLowerCase().includes(q.toLowerCase())), [msgs, tab, q]);
  const pg = usePaged(rows, `${tab}|${q}`);
  const unread = (msgs || []).filter((m) => !m.read).length;
  return (
    <div data-testid="contact-messages-page">
      <PageHeader eyebrow="Membership" title="Contact Messages" description={`Messages sent through the Contact us form.${unread ? ` ${unread} unread.` : ""} Mark each one resolved once it has been handled.`} />
      <div className="flex flex-col md:flex-row gap-3 md:items-center mb-4">
        <div className="flex rounded-md border border-[#E5E7EB] bg-white overflow-hidden" data-testid="contact-tabs">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`contact-tab-${k}`} className={`h-10 px-4 text-sm font-semibold transition-colors ${tab === k ? "bg-[#111] text-white" : "hover:bg-gray-50"}`}>{l} <span className="opacity-70">({counts[k]})</span></button>)}
        </div>
        <div className="relative flex-1 max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or message" className="pl-9 h-10 bg-white" data-testid="contact-search" /></div>
      </div>
      {!msgs ? <div className="text-gray-500">Loading…</div> : <>
        <div className="space-y-2">
          {pg.items.map((m) => <Message key={m.id} m={m} open={open === m.id} busy={busy} onToggle={() => toggle(m)} onResolve={(v) => resolve(m, v)} onDelete={() => setDeleting(m)} />)}
          {!rows.length && <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="contact-empty">{tab === "open" ? "No open messages. Everything has been handled." : "No messages here."}</div>}
        </div>
        <Pager {...pg} testId="contact-pager" />
      </>}
      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} title="Delete this message?" description="This permanently removes the message. It does not send an email."
        confirmLabel="Delete message" testId="contact-delete" onConfirm={async () => { await api.delete(`/contact-messages/${deleting.id}`); toast.success("Message deleted"); changed(); load(); }} />
    </div>
  );
}
